/**
 * EditClusterModal Component
 *
 * Modal for editing existing clusters.
 * Allows updating name, description, tags, and selected items.
 */

import { useState, useEffect, useMemo } from 'react';
import { Loader2, Layers } from 'lucide-react';
import { Modal, Button, Input, Badge } from '@/components/ui';
import { ClusterItemSelector } from './ClusterItemSelector';
import { calculateClusterStats, updateCluster } from '@/services/clusterService';
import type { Cluster, ClusterItem, Region, Country, Location } from '@simcheck/shared-types';

export interface EditClusterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  cluster: Cluster;
  regions: Region[];
  countries: Country[];
  locations: Location[];
}

export function EditClusterModal({
  isOpen,
  onClose,
  onSuccess,
  cluster,
  regions,
  countries,
  locations,
}: EditClusterModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedItems, setSelectedItems] = useState<ClusterItem[]>([]);
  const [tagsInput, setTagsInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill form when cluster changes
  useEffect(() => {
    if (cluster && isOpen) {
      setName(cluster.name);
      setDescription(cluster.description || '');
      setSelectedItems(cluster.items || []);
      setTagsInput((cluster.tags || []).join(', '));
      setError(null);
    }
  }, [cluster, isOpen]);

  // Calculate preview stats
  const stats = useMemo(() => {
    return calculateClusterStats(selectedItems, regions, countries, locations);
  }, [selectedItems, regions, countries, locations]);

  // Handle save
  const handleSave = async () => {
    // Validate
    if (!name.trim()) {
      setError('Please enter a cluster name');
      return;
    }

    if (selectedItems.length === 0) {
      setError('Please select at least one region, country, or location');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const tags = tagsInput
        ? tagsInput.split(',').map(t => t.trim()).filter(Boolean)
        : undefined;

      await updateCluster(cluster.id, {
        name: name.trim(),
        description: description.trim() || undefined,
        items: selectedItems,
        tags,
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update cluster');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Cluster"
      description="Update this cluster's configuration"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            disabled={saving || !name.trim() || selectedItems.length === 0}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Save Changes
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 rounded bg-destructive/10 text-destructive text-sm">
            {error}
          </div>
        )}

        {/* Name */}
        <Input
          label="Cluster Name"
          placeholder="e.g., Europe Major Cities"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        {/* Description */}
        <Input
          label="Description"
          placeholder="Optional description of this cluster"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        {/* Tags */}
        <Input
          label="Tags"
          placeholder="production, roaming, priority"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          helperText="Comma-separated list of tags for filtering"
        />

        {/* Item Selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Cluster Items <span className="text-destructive">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            Select regions, countries, or individual locations to include in this cluster.
          </p>
          <ClusterItemSelector
            regions={regions}
            countries={countries}
            locations={locations}
            selectedItems={selectedItems}
            onSelectionChange={setSelectedItems}
            maxHeight="300px"
          />
        </div>

        {/* Preview Stats */}
        {selectedItems.length > 0 && (
          <div className="p-4 rounded-lg bg-muted/50 border">
            <div className="flex items-center gap-2 mb-3">
              <Layers className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Cluster Preview</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-2xl font-mono font-bold text-foreground">
                  {stats.locationCount}
                </div>
                <div className="text-xs text-muted-foreground">
                  Total Locations
                </div>
              </div>
              <div>
                <div className="text-2xl font-mono font-bold text-foreground">
                  {stats.modemCount}
                </div>
                <div className="text-xs text-muted-foreground">
                  Total Modems
                </div>
              </div>
            </div>
            {stats.locationCount === 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                No active locations in selected items
              </p>
            )}
          </div>
        )}

        {/* Selected Items Summary */}
        {selectedItems.length > 0 && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Selected Items</label>
            <div className="flex flex-wrap gap-2">
              {selectedItems.map(item => (
                <Badge
                  key={`${item.type}:${item.id}`}
                  variant="secondary"
                  className="text-xs"
                >
                  {item.type === 'region' && '🌍'}
                  {item.type === 'country' && '🏳️'}
                  {item.type === 'location' && '📍'}
                  <span className="ml-1">{item.displayName}</span>
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default EditClusterModal;
